/**
 * The small pieces the Systems view, the system and integration pages and their peeks share (docs/systems.md
 * "Systems view"): the range pills, a status as a dot and a word, the runs as bars over time, a system pair "ERP → WMS",
 * an integration as one row, and a section with its kicker. Same visual language as Now: neutral surfaces, status as
 * dots and thin strokes, numbers in tabular figures.
 */
import { useSearchParams } from '../lib/liveparams.ts';
import { setNameMode, useNameMode } from '../lib/namemode.ts';
import { Segmented } from '../components/FilterChips.tsx';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import dayjs from 'dayjs';
import type { Integration, RunBar } from '../lib/api.ts';
import { useEnv } from '../lib/envs.tsx';
import { count } from '../lib/format.ts';
import { age } from '../lib/freshness.ts';
import { cadenceLabel } from '../lib/integrations.ts';
import { RANGES, RANGE_LABEL, STATUS_WORD, integrationPath, systemPath, type SysRange } from '../lib/systemsview.ts';
import { stripSystem, systemName } from '../lib/systems.ts';
import { Kicker, Pill, SystemBadge, cx } from '../design/primitives.tsx';
import './systems.css';

/** 1 h · 24 h · 7 d. */
export function RangePills({ value, onChange, className }: { value: SysRange; onChange: (r: SysRange) => void; className?: string }) {
  return (
    <div role="group" aria-label="Time range" className={cx('inline-flex items-center gap-0.5 p-0.5 rounded-full bg-n3', className)}>
      {RANGES.map((r) => <Pill key={r} active={r === value} onClick={() => onChange(r)}>{RANGE_LABEL[r]}</Pill>)}
    </div>
  );
}

const DOT: Record<Integration['status'], string> = { ok: 'bg-ok', failing: 'bg-fail', quiet: 'bg-warn' };

export function StatusMark({ status, className, quietOk }: { status: Integration['status'] | null; className?: string; quietOk?: boolean }) {
  if (!status) return <span className={cx('text-small text-n7', className)}>No runs</span>;
  return (
    <span className={cx('inline-flex items-center gap-1.5 text-small whitespace-nowrap', status === 'failing' ? 'text-fail' : status === 'quiet' ? 'text-warn' : quietOk ? 'text-n8' : 'text-n9', className)}>
      <i className={cx('size-2 rounded-full shrink-0', DOT[status])} aria-hidden="true" />
      {STATUS_WORD[status]}
    </span>
  );
}

export const statusDotClass = (s: Integration['status']) => DOT[s];

/** Every system a process goes through, in order: CLO → MDH → PRO. */
export function Chain({ path, size = 20 }: { path: string[]; size?: 20 | 24 | 28 }) {
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0" data-chain={path.join('>')}>
      {path.map((s, k) => (
        <span key={`${s}${k}`} className="inline-flex items-center gap-1.5">
          {k > 0 && <span className="text-n7 shrink-0" aria-label="to">→</span>}
          <SystemBadge code={s} size={size} />
        </span>
      ))}
    </span>
  );
}

/** A system as a link to its page: badge and name. */
export function SystemLink({ code, size = 20, name = true, className }: { code: string; size?: 20 | 24 | 28; name?: boolean; className?: string }) {
  const { link } = useEnv();
  return (
    <Link to={link(systemPath(code))} className={cx('inline-flex items-center gap-1.5 min-w-0 no-underline text-n10 hover:underline underline-offset-2', className)} onClick={(e) => e.stopPropagation()}>
      <SystemBadge code={code} size={size} />
      {name && <span className="truncate">{systemName(code)}</span>}
    </Link>
  );
}

/** "ERP → WMS" for a two-sided integration; "from ERP" / "to EDI" / "within ERP" for a one-sided one. */
export function Pair({ from, to, size = 20, names = false }: { from: string | null; to: string | null; size?: 20 | 24 | 28; names?: boolean }) {
  const arrow = <span className="text-n7 shrink-0" aria-label="to">→</span>;
  if (from && to && from !== to) return <span className="inline-flex items-center gap-1.5 min-w-0"><SystemBadge code={from} size={size} />{names && <span className="truncate">{systemName(from)}</span>}{arrow}<SystemBadge code={to} size={size} />{names && <span className="truncate">{systemName(to)}</span>}</span>;
  const one = (from ?? to)!;
  const word = from && to ? 'within' : from ? 'reads' : 'writes';
  return <span className="inline-flex items-center gap-1.5 min-w-0"><SystemBadge code={one} size={size} />{names && <span className="truncate">{systemName(one)}</span>}<span className="text-micro text-n7">{word}</span></span>;
}

/**
 * The runs of a window as bars, failed runs as the red part of each (the sparkline of Now's headline, larger); bars
 * before the runs the Vaults listed are hatched. The first and last times label the axis.
 */
export function RunBars({ bars, height = 64, className, compact }: { bars: RunBar[]; height?: number; className?: string; compact?: boolean }) {
  const max = Math.max(1, ...bars.map((b) => b.runs));
  const h = (n: number) => (n > 0 ? Math.max(2, Math.round((n / max) * height)) : 1);
  const total = bars.reduce((n, b) => n + b.runs, 0);
  const failed = bars.reduce((n, b) => n + b.failed, 0);
  const span = bars.length ? bars[bars.length - 1]!.end - bars[0]!.start : 0;
  const fmt = (t: number) => (span > 36 * 3_600_000 ? dayjs(t).format('ddd D.M.') : dayjs(t).format('HH:mm'));
  return (
    <div className={cx('sys-bars-wrap', className)}>
      <div className={cx('sys-bars', compact && 'compact')} style={{ height }} role="img" aria-label={`${count(total)} runs, ${count(failed)} with failures`}>
        {bars.map((b) => (
          <i key={b.start} className={cx(!b.known && 'unknown')} style={{ height: b.known ? h(b.runs) : height }}
            title={b.known ? `${fmt(b.start)}–${dayjs(b.end).format('HH:mm')}: ${count(b.runs)} ${b.runs === 1 ? 'run' : 'runs'}${b.failed ? `, ${count(b.failed)} failed` : ''}` : 'Older runs were not listed'}>
            {b.known && b.failed > 0 && <b style={{ height: h(b.failed) }} />}
          </i>
        ))}
      </div>
      {bars.length > 0 && (
        <div className="sys-bars-axis" aria-hidden="true">
          <span>{fmt(bars[0]!.start)}</span>
          <span>now</span>
        </div>
      )}
    </div>
  );
}

/** A section: the kicker with a fact on the right, then its content. */
export function Section({ title, fact, action, children, className, id }: { title: ReactNode; fact?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; id?: string }) {
  return (
    <section className={cx('flex flex-col gap-3 min-w-0', className)} aria-labelledby={id}>
      <div className="flex items-center gap-3 min-h-7">
        <Kicker className="truncate"><span id={id}>{title}</span>{fact !== undefined && fact !== null && <span className="text-n7"> · {fact}</span>}</Kicker>
        {action && <span className="ml-auto shrink-0">{action}</span>}
      </div>
      {children}
    </section>
  );
}

/**
 * An integration as one row: its status dot, its name (without the [PREFIX]), where data goes, its runs in the window,
 * when it last ran and how often, and its state. The whole row opens the integration's page.
 */
export function IntegrationRow({ i, now, other, onOpen }: {
  i: Integration; now: number;
  /** Seen from one system: show only the other end ("from WEB", "to WMS"). */
  other?: { dir: 'in' | 'out' | 'within' };
  /** Instead of the link (a peek opening another peek). */
  onOpen?: (process: string) => void;
}) {
  const { link } = useEnv();
  const cadence = cadenceLabel(i.cadenceMs);
  const body = (
    <>
      <i className={cx('size-2 rounded-full shrink-0', DOT[i.status])} aria-hidden="true" />
      <span className="sys-row-name">
        <span className={cx('truncate font-medium', i.status === 'failing' ? 'text-fail' : 'text-n10')} title={i.process}>{stripSystem(i.process)}</span>
        <span className="sys-row-pair">
          {other ? (
            other.dir === 'within' ? <span className="text-micro text-n7">within the system</span>
              : <span className="inline-flex items-center gap-1.5 min-w-0"><span className="text-micro text-n7">{other.dir === 'in' ? 'from' : 'to'}</span>
                {(other.dir === 'in' ? i.from : i.to) ? <SystemBadge code={(other.dir === 'in' ? i.from : i.to)!} size={20} /> : <span className="text-micro text-n7">nothing logged</span>}</span>
          ) : i.path && i.path.length > 2 ? <Chain path={i.path} /> : <Pair from={i.from} to={i.to} />}
        </span>
      </span>
      <span className="sys-row-num tabular-nums">{count(i.runs)}<span className="text-n7"> {i.runs === 1 ? 'run' : 'runs'}</span></span>
      <span className="sys-row-last text-n8">{i.lastRun ? `${age(i.lastRun, now)} ago` : '—'}{cadence && <span className="text-n7"> · {cadence}</span>}</span>
      <StatusMark status={i.status} quietOk className="sys-row-status" />
    </>
  );
  if (onOpen) return <button type="button" className="sys-row" data-integration={i.process} onClick={() => onOpen(i.process)}>{body}</button>;
  return <Link to={link(integrationPath(i.process))} className="sys-row" data-integration={i.process}>{body}</Link>;
}

/** A primary action, set like the peeks' buttons: a dark pill. */
export function ActionLink({ to, children, quiet, className }: { to: string; children: ReactNode; quiet?: boolean; className?: string }) {
  return (
    <Link to={to} className={cx('inline-flex items-center gap-2 h-9 px-4 rounded-full text-small font-semibold no-underline whitespace-nowrap transition-colors duration-[var(--t-micro)]',
      quiet ? 'bg-n3 text-n10 hover:bg-n4' : 'bg-n10 text-n1 hover:opacity-90', className)}>
      {children}
    </Link>
  );
}

/** Business or technical names for everything on the systems side (a remembered preference of the viewer). */
export function NameToggle() {
  const pref = useNameMode();
  const [params, setParams] = useSearchParams();
  const url = params.get('names');
  const mode = url === 'technical' || url === 'business' ? url : pref;
  // A step in history: Back returns to the names the view had
  const change = (next: 'business' | 'technical') => {
    // The entry we leave keeps the names it showed (else coming Back would take the new preference)
    const here = new URL(window.location.href);
    if (here.searchParams.get('names') !== mode) {
      here.searchParams.set('names', mode);
      window.history.replaceState(window.history.state, '', here.pathname + here.search + here.hash);
    }
    setNameMode(next);
    setParams((p) => { const out = new URLSearchParams(p); out.set('names', next); return out; });
  };
  return (
    <Segmented ariaLabel="Names" label="Names" value={mode} onChange={change}
      options={[['business', 'Business'], ['technical', 'Technical']] as const} />
  );
}
